import {CellRangeEventArgs, FlexGrid, FormatItemEventArgs, CellRange} from "@mescius/wijmo.grid";
import {
    CollectionView, ICollectionView,
    isDate,
    isUndefined,
    NotifyCollectionChangedAction,
    NotifyCollectionChangedEventArgs
} from "@mescius/wijmo";
import {EventEmitter} from "@angular/core";
import {UndoStack} from '@mescius/wijmo.undo';

export class WjGridChangeTracker {

    private _originalValues = new Map<any, any>();
    private _originalValue: any = undefined;

    public onCellChanged = new EventEmitter<CellRangeEventArgs>();

    constructor(private grid: FlexGrid) {
        grid.beginningEdit.addHandler(this.cellChanging.bind(this));
        grid.cellEditEnded.addHandler(this.cellChanged.bind(this));
        grid.pastingCell.addHandler(this.cellChanging.bind(this));
        grid.pastedCell.addHandler(this.cellChanged.bind(this));
        grid.deletedRow.addHandler(this.rowDeleted.bind(this));
        grid.rowAdded.addHandler(this.rowAdded.bind(this));

        const itemsSource = grid.itemsSource;
        if (itemsSource instanceof CollectionView) {
            itemsSource.collectionChanged.addHandler(this.collectionChanged.bind(this));
        }
    }

    hasChange(item: any, binding: string | null) {
        if (binding == null) return false;
        item = this._originalValues.get(item);
        return item != null && !isUndefined(item[binding]);
    }

    hasChangeAnywhere(item: any) {
        item = this._originalValues.get(item);
        return item != null;
    }

    // handles cell changing (edit or paste)
    private cellChanging(s: FlexGrid, e: CellRangeEventArgs) {
        this._originalValue = s.getCellData(e.row, e.col, false);
    }

    // handles cell changed (edit or paste)
    private cellChanged(s: FlexGrid, e: CellRangeEventArgs) {
        const newValue = s.getCellData(e.row, e.col, false);
        const wasChanged = this.storeChange(e.getRow().dataItem, e.getColumn().binding!, this._originalValue, newValue);
        //if (wasChanged) {
            this.onCellChanged.emit(e);
        //}
    }

    public notifyCellChange(row: number, binding: string, originalValue: any, newValue: any) {
        const dataItem = this.grid._getRowData(row);
        // const wasChanged = this.storeChange(dataItem, binding, this._originalValue, newValue);
        const wasChanged = this.storeChange(dataItem, binding, originalValue, newValue);
        const cellRangeChange = new CellRangeEventArgs(this.grid.cells, new CellRange(row, 0))
        if (wasChanged) this.onCellChanged.emit(cellRangeChange);
    }

    public notifyCellItemChange(dataItem: any, binding: string, originalValue: any, newValue: any) {
        // const wasChanged = this.storeChange(dataItem, binding, this._originalValue, newValue);
        const row = this.grid.collectionView.items.indexOf(dataItem);
        const wasChanged = this.storeChange(dataItem, binding, originalValue, newValue);
        const cellRangeChange = new CellRangeEventArgs(this.grid.cells, new CellRange(row, 0))
        if (wasChanged) this.onCellChanged.emit(cellRangeChange);
    }

    // TODO: this should be combined with the undostack
    cellRangeChanged(s: FlexGrid, e: CellRange) {
        for (let row = e.topRow; row <= e.bottomRow; row++) {
            for (let col = e.leftCol; col <= e.rightCol; col++) {
                const item = s.getCellData(row, col, false);
                const binding = s.columns[col].binding;
                if (binding) {
                    const originalValue = this._originalValues.get(item)?.[binding];
                    const newValue = s.getCellData(row, col, false);
                    // this.storeChange(item, binding, originalValue, newValue);
                }
            }
        }
    }

    private storeChange(item: any, binding: string, xoriginalValue: any, newValue: any): boolean {
        // get the reference in which we store the original values
        // (these reference exist only when there really was a change!)
        let orgValuesItemMap = this._originalValues.get(item);
        if (orgValuesItemMap == null) {
            // create when not exists yet
            orgValuesItemMap = {};
            this._originalValues.set(item, orgValuesItemMap);
        }

        // get the original value from the item if possible
        let orgValue = orgValuesItemMap[binding];

        if (this.isSameValue(orgValue, newValue)) {
            delete orgValuesItemMap[binding];
            if (Object.keys(orgValuesItemMap).length == 0) {
                this._originalValues.delete(item);
            }
            return false;
        } else {
            // store the original value if it is different - used 'null' for all originally undefined values
            orgValuesItemMap[binding] = xoriginalValue ?? null;
            return true;
        }
    }

    // store a change into the _originalValues map
    private storeChange1(item: any, binding: string, originalValue: any, newValue: any): boolean {
        // get the item with the original values
        let editItem = this._originalValues.get(item);
        if (editItem == null) {
            editItem = {};
            this._originalValues.set(item, editItem);
        }

        // get the original value from the item if possible
        let editValue = editItem[binding];
        if (!isUndefined(editValue)) {
            originalValue = editValue;
        }

        // store or clear the change
        if (this.isSameValue(originalValue, newValue)) {
            delete editItem[binding];
            if (Object.keys(editItem).length == 0) {
                this._originalValues.delete(item);
            }
            return false;
        } else {
            // store the original value if it is different - used 'null' for all originally undefined values
            editItem[binding] = originalValue ?? null;
            return true;
        }
    }

    // compare two values taking dates into account
    private isSameValue(item1: any, item2: any) {
        if (item1 === item2) return true;
        if (isDate(item1) && isDate(item2)) {
            return item1.getTime() == item2.getTime();
        }
        return false;
    }

    private rowDeleted(s: FlexGrid, e: CellRangeEventArgs) {
        this._originalValues.delete(e.getRow().dataItem);
        this.onCellChanged.emit(e);
    }

    private rowAdded(s: FlexGrid, e: CellRangeEventArgs) {
        this._originalValues.set(e.getRow().dataItem, {});
        this.onCellChanged.emit(e);
    }

    private collectionChanged(s: ICollectionView, e: NotifyCollectionChangedEventArgs) {
        if (e.action === NotifyCollectionChangedAction.Add) {
            this._originalValues.set(e.item, {});
            this.onCellChanged.emit(undefined);
        } else if (e.action === NotifyCollectionChangedAction.Remove) {
            this._originalValues.delete(e.item);
            this.onCellChanged.emit(undefined);
        }
    }

    private gridFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        if (e.panel == s.rowHeaders) {
            // nothing special to do for row headers
        } else if (e.panel == s.cells) {
            const column = this.grid.columns[e.col];
            const item = e.getRow().dataItem;

            const changed = this.hasChange(item, column.binding);
            e.cell.classList.toggle('cell-changed', changed);
        }
        e.cell.classList.toggle('row-changed', this.hasChangeAnywhere(e.getRow().dataItem));
    }

    connectUndoStack(undoStack: UndoStack) {
        // TODO....
    }

    static addChangeTracker(grid: FlexGrid): WjGridChangeTracker {
        const tracker = new WjGridChangeTracker(grid);
        grid.formatItem.addHandler(tracker.gridFormatHandler.bind(tracker));
        return tracker;
    }
}
